import React, { useState, useEffect } from 'react';
import { FaTag, FaEdit, FaMoneyBillWave, FaClock, FaTimes, FaUser } from 'react-icons/fa';
import apiClient from '../../utils/axiosPetition';
import Swal from 'sweetalert2';

function formatDate(date) {
  if (!date) return 'Sin editar';
  const d = new Date(date);
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const año = d.getFullYear();
  const hora = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dia}/${mes}/${año} ${hora}:${min}`;
}

function formatCurrency(valor) {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(valor);
}

function formatAuditor(servicio, tipo) {
  const emailKey = tipo === 'creado' ? 'creadoPorEmail' : 'editadoPorEmail';
  const refKey = tipo === 'creado' ? 'creadoPor' : 'editadoPor';

  if (servicio[emailKey]) return servicio[emailKey];
  if (servicio[refKey]?.username) return servicio[refKey].username;
  if (tipo === 'creado') return 'Sistema (automático)';
  return 'Sin registrar';
}

const GestionPrecios = () => {
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [servicioEdit, setServicioEdit] = useState(null);
  const [nuevoValor, setNuevoValor] = useState('');
  const [nuevaDescripcion, setNuevaDescripcion] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchServicios();
  }, []);

  const fetchServicios = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/precios/gestion');
      setServicios(res.data);
    } catch (err) {
      Swal.fire('Error', 'No se pudieron cargar los precios.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (servicio) => {
    setServicioEdit(servicio);
    setNuevoValor(servicio.valor);
    setNuevaDescripcion(servicio.descripcion);
    setError('');
    setModalOpen(true);
  };

  const handleGuardar = async () => {
    if (!nuevaDescripcion.trim()) {
      setError('La descripción no puede estar vacía');
      return;
    }
    if (!nuevoValor || isNaN(nuevoValor) || Number(nuevoValor) <= 0) {
      setError('Ingresa un valor válido mayor a 0');
      return;
    }

    const cambios = {};
    if (nuevaDescripcion.trim() !== servicioEdit.descripcion) cambios.descripcion = nuevaDescripcion.trim();
    if (Number(nuevoValor) !== servicioEdit.valor) cambios.valor = Number(nuevoValor);

    if (Object.keys(cambios).length === 0) {
      setError('No realizaste ningún cambio.');
      return;
    }

    try {
      const res = await apiClient.put(`/api/precios/${servicioEdit._id}`, cambios);
      setServicios(prev => prev.map(s => s._id === servicioEdit._id ? res.data : s));
      Swal.fire('¡Actualizado!', 'El precio fue actualizado correctamente.', 'success');
      setModalOpen(false);
      setServicioEdit(null);
      setNuevoValor('');
      setNuevaDescripcion('');
      setError('');
    } catch (err) {
      const msg = err.response?.data?.message || 'No se pudo actualizar el precio.';
      Swal.fire('Error', msg, 'error');
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1f94c2] to-[#205087] flex items-center justify-center shadow-lg shadow-blue-500/25">
              <FaTag className="text-white w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Gestión de Precios</h1>
              <p className="text-gray-500 text-sm mt-0.5 max-w-2xl">
                Precios oficiales conectados a la plataforma. Solo puede editar valor y descripción.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
            <h2 className="font-semibold text-gray-700 flex items-center gap-2">
              <FaMoneyBillWave className="text-[#1f94c2]" />
              Catálogo de precios ({servicios.length})
            </h2>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-12 h-12 border-4 border-[#1f94c2]/20 border-t-[#1f94c2] rounded-full animate-spin mb-4" />
                <p className="text-gray-500 font-medium">Cargando precios...</p>
              </div>
            ) : servicios.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16">
                <p className="text-gray-500 font-medium">No hay precios configurados</p>
              </div>
            ) : (
              <table className="w-full min-w-[1000px]">
                <thead>
                  <tr className="bg-gradient-to-r from-[#205087] to-[#1f94c2]">
                    <th className="py-4 px-6 text-left text-sm font-semibold text-white">Servicio</th>
                    <th className="py-4 px-6 text-left text-sm font-semibold text-white">Descripción</th>
                    <th className="py-4 px-6 text-left text-sm font-semibold text-white">Valor</th>
                    <th className="py-4 px-6 text-left text-sm font-semibold text-white">Editado por</th>
                    <th className="py-4 px-6 text-left text-sm font-semibold text-white">Última edición</th>
                    <th className="py-4 px-6 text-center text-sm font-semibold text-white">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {servicios.map((servicio) => (
                    <tr key={servicio._id} className="hover:bg-blue-50/50 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1f94c2]/10 to-[#205087]/10 flex items-center justify-center">
                            <FaTag className="text-[#1f94c2] w-4 h-4" />
                          </div>
                          <span className="font-semibold text-gray-800">{servicio.nombre}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-gray-600 text-sm">{servicio.descripcion}</span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-green-50 text-green-700 font-bold text-sm">
                          {formatCurrency(servicio.valor)}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <FaUser className="w-3.5 h-3.5 shrink-0 text-[#1f94c2]" />
                          <span className="break-all">{formatAuditor(servicio, 'editado')}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                          <FaClock className="w-3.5 h-3.5" />
                          <span>{formatDate(servicio.ultimaEdicion)}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center">
                          <button
                            type="button"
                            className="flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-orange-400 text-white px-4 py-2 rounded-lg shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-200 font-medium text-sm"
                            onClick={() => handleEditar(servicio)}
                          >
                            <FaEdit className="w-3.5 h-3.5" />
                            Editar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {modalOpen && servicioEdit && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden">
            <div className="bg-gradient-to-r from-[#1f94c2] to-[#205087] px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <FaEdit className="text-white w-4 h-4" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Editar precio</h3>
                </div>
                <button
                  type="button"
                  className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                  onClick={() => setModalOpen(false)}
                >
                  <FaTimes className="text-white w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Servicio</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 text-gray-600 cursor-not-allowed"
                  value={servicioEdit.nombre}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Descripción</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1f94c2] focus:border-transparent transition-all text-gray-800"
                  value={nuevaDescripcion}
                  onChange={(e) => setNuevaDescripcion(e.target.value)}
                  maxLength={120}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Valor (COP)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                  <input
                    type="number"
                    min="1"
                    className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1f94c2] focus:border-transparent transition-all text-gray-800"
                    value={nuevoValor}
                    onChange={(e) => setNuevoValor(e.target.value)}
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                  <FaTimes className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                type="button"
                className="w-full bg-gradient-to-r from-[#1f94c2] to-[#205087] text-white font-semibold py-3.5 rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200 mt-2"
                onClick={handleGuardar}
              >
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionPrecios;
